/**
 * Der Filterzustand von Auto Q&A — er steht in der Adresse, nicht im Zustand.
 *
 * Paket 19, Abschnitt B6: „Ein Link auf eine gefilterte Sicht muss teilbar
 * sein." Deshalb liegt der Filter in der Suchleiste (`?zustand=laeuft&…`) und
 * nicht in einem `useState`, das beim Neuladen verschwindet — dieselbe
 * Entscheidung wie bei `?bereich=` auf der Einstellungsseite.
 *
 * Hin und zurück ohne Verlust: Was `sucheAusFilter` schreibt, liest
 * `filterAusSuche` wieder ein. Vorgaben werden **weggelassen** statt
 * ausgeschrieben — eine Adresse soll den Unterschied zur Vorgabe zeigen, nicht
 * die Vorgabe wiederholen.
 */

import { QA_AUFTRAG_ZUSTAENDE, type QaAuftragZustand } from "@cubic/shared";

export const ZEITRAEUME = ["24h", "7t", "30t"] as const;
export type Zeitraum = (typeof ZEITRAEUME)[number];

export const EINTRAEGE_OPTIONEN = [25, 50, 100, 200] as const;
export type EintraegeAnzahl = (typeof EINTRAEGE_OPTIONEN)[number];

export const ZEITRAUM_LABEL: Readonly<Record<Zeitraum, string>> = {
  "24h": "24 Stunden",
  "7t": "7 Tage",
  "30t": "30 Tage",
};

export const ZEITRAUM_TAGE: Readonly<Record<Zeitraum, number>> = {
  "24h": 1,
  "7t": 7,
  "30t": 30,
};

export type AutoQaFilter = {
  projektId?: string | undefined;
  /** Leer heißt: die Vorgabe aus `VORGABE_ZUSTAENDE`. */
  zustand: QaAuftragZustand[];
  automationId?: string | undefined;
  zeitraum: Zeitraum;
  grenze: EintraegeAnzahl;
  nurAufhalter: boolean;
};

/**
 * Vorgabe: alle Zustände. Auch sauber abgeschlossene Aufträge gehören zum
 * Entwicklungsstand und dürfen nicht erst über einen versteckten URL-Filter
 * sichtbar werden.
 */
export const VORGABE_ZUSTAENDE: QaAuftragZustand[] = [
  ...QA_AUFTRAG_ZUSTAENDE,
];

export const VORGABE_ZEITRAUM: Zeitraum = "7t";
export const VORGABE_EINTRAEGE: EintraegeAnzahl = 100;

export const ZUSTANDSFILTER = [
  {
    wert: "alle",
    label: "Alle Zustände",
    zustaende: QA_AUFTRAG_ZUSTAENDE,
  },
  {
    wert: "offen",
    label: "Offen",
    zustaende: ["gebremst", "wartet", "laeuft"],
  },
  { wert: "gebremst", label: "Sammelt", zustaende: ["gebremst"] },
  { wert: "wartet", label: "In Warteschlange", zustaende: ["wartet"] },
  { wert: "laeuft", label: "Läuft", zustaende: ["laeuft"] },
  { wert: "fertig", label: "Sauber", zustaende: ["fertig"] },
  {
    wert: "fertig_mit_aufhaltern",
    label: "Hält auf",
    zustaende: ["fertig_mit_aufhaltern"],
  },
  {
    wert: "gescheitert",
    label: "Gescheitert",
    zustaende: ["gescheitert"],
  },
  {
    wert: "abgebrochen",
    label: "Abgebrochen",
    zustaende: ["abgebrochen"],
  },
] as const satisfies readonly {
  wert: string;
  label: string;
  zustaende: readonly QaAuftragZustand[];
}[];

export type ZustandsfilterWert =
  | (typeof ZUSTANDSFILTER)[number]["wert"]
  | "eigene";

function gleicheZustaende(
  links: readonly QaAuftragZustand[],
  rechts: readonly QaAuftragZustand[],
): boolean {
  return (
    links.length === rechts.length &&
    links.every((zustand) => rechts.includes(zustand))
  );
}

export function zustandsfilterAusZustaenden(
  zustaende: readonly QaAuftragZustand[],
): ZustandsfilterWert {
  return (
    ZUSTANDSFILTER.find((option) =>
      gleicheZustaende(option.zustaende, zustaende),
    )?.wert ?? "eigene"
  );
}

export function zustaendeAusZustandsfilter(
  wert: string,
): QaAuftragZustand[] {
  const option = ZUSTANDSFILTER.find((eintrag) => eintrag.wert === wert);
  return option ? [...option.zustaende] : [...VORGABE_ZUSTAENDE];
}

function istZustand(wert: string): wert is QaAuftragZustand {
  return (QA_AUFTRAG_ZUSTAENDE as readonly string[]).includes(wert);
}

/**
 * Ein Suchwert als Text — egal, was der Router daraus gemacht hat.
 *
 * **Gelernt am 14.08.2026:** TanStack Router wandelt numerisch aussehende
 * Suchwerte um, bevor irgendjemand sie sieht. Aus `?aufhalter=1` wird die
 * Zahl `1`, und ein Vergleich `=== "1"` scheitert lautlos — der Schalter stand
 * auf aus, obwohl die Adresse ihn auf an setzte. Ein Filter, der nur Text
 * verträgt, ist an dieser Stelle kein Filter.
 */
function alsText(wert: unknown): string {
  if (wert === undefined || wert === null) return "";
  return String(wert);
}

export function filterAusSuche(roh: Record<string, unknown>): AutoQaFilter {
  const zustaende = alsText(roh["zustand"])
    .split(",")
    .map((s) => s.trim())
    .filter(istZustand);

  const zeitraumRoh = alsText(roh["zeitraum"]);
  const zeitraum = ZEITRAEUME.includes(zeitraumRoh as Zeitraum)
    ? (zeitraumRoh as Zeitraum)
    : VORGABE_ZEITRAUM;

  const projekt = alsText(roh["projekt"]);
  const automation = alsText(roh["automation"]);
  const eintraegeRoh = Number(alsText(roh["eintraege"]));
  const grenze = EINTRAEGE_OPTIONEN.includes(
    eintraegeRoh as EintraegeAnzahl,
  )
    ? (eintraegeRoh as EintraegeAnzahl)
    : VORGABE_EINTRAEGE;

  return {
    ...(projekt ? { projektId: projekt } : {}),
    zustand: zustaende.length > 0 ? zustaende : [...VORGABE_ZUSTAENDE],
    ...(automation ? { automationId: automation } : {}),
    zeitraum,
    grenze,
    nurAufhalter: alsText(roh["aufhalter"]) === "1",
  };
}

export function sucheAusFilter(
  filter: AutoQaFilter,
): Record<string, string | number | undefined> {
  const gleichWieVorgabe =
    filter.zustand.length === VORGABE_ZUSTAENDE.length &&
    VORGABE_ZUSTAENDE.every((z) => filter.zustand.includes(z));

  return {
    ...(filter.projektId ? { projekt: filter.projektId } : {}),
    ...(gleichWieVorgabe || filter.zustand.length === 0
      ? {}
      : { zustand: filter.zustand.join(",") }),
    ...(filter.automationId ? { automation: filter.automationId } : {}),
    ...(filter.zeitraum === VORGABE_ZEITRAUM
      ? {}
      : { zeitraum: filter.zeitraum }),
    ...(filter.grenze === VORGABE_EINTRAEGE
      ? {}
      : { eintraege: filter.grenze }),
    ...(filter.nurAufhalter ? { aufhalter: 1 } : {}),
  };
}

/** Der Beginn des Zeitraums, gemessen von jetzt. */
export function seitAus(zeitraum: Zeitraum, jetzt = Date.now()): Date {
  return new Date(jetzt - ZEITRAUM_TAGE[zeitraum] * 24 * 60 * 60 * 1_000);
}

/**
 * Der Median, nicht der Mittelwert (B2).
 *
 * Ein hängengebliebener Lauf verschiebt den Mittelwert und behauptet ein
 * Problem, das keines ist. Leere Liste ergibt `null` — und `null` ist ein
 * Strich, keine Null: „noch nichts gelaufen" ist etwas anderes als „dauerte
 * null Sekunden".
 */
export function median(werte: readonly number[]): number | null {
  const sortiert = [...werte].sort((a, b) => a - b);
  if (sortiert.length === 0) return null;
  const mitte = Math.floor(sortiert.length / 2);
  return sortiert.length % 2 === 1
    ? sortiert[mitte]!
    : Math.round((sortiert[mitte - 1]! + sortiert[mitte]!) / 2);
}

/** Dauer in Millisekunden als kurze Angabe: „2 min 12 s", „47 s". */
export function dauerText(ms: number | null): string {
  if (ms === null) return "—";
  const sekunden = Math.round(ms / 1_000);
  if (sekunden < 60) return `${sekunden} s`;
  const minuten = Math.floor(sekunden / 60);
  const rest = sekunden % 60;
  return rest === 0 ? `${minuten} min` : `${minuten} min ${rest} s`;
}
